import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Truck, Loader2, X, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMATOS = [
  { v: "doble_a5", t: "A4 horizontal, doble vía (A5)", d: "Original para el repartidor y duplicado para el comercio, lado a lado." },
  { v: "a4", t: "A4 vertical", d: "Una orden por hoja A4." },
  { v: "ticket", t: "Ticket 80 mm", d: "Para impresora térmica." },
];
const MODOS = [
  { v: "con_factura", t: "Imprimir al emitir la factura", d: "La orden de delivery sale automáticamente cuando se emite una factura con producto de delivery." },
  { v: "aparte", t: "Emitir la orden aparte", d: "La orden se imprime con un botón desde la factura, cuando lo necesites." },
];

/** Configuración por empresa: Delivery (productos, formato de la orden y cuándo se imprime). */
export default function DeliveryConfigCard({ token, logoTipo, puedeEditar }) {
  const [cfg, setCfg] = useState({ productos: [], formato: "doble_a5", imprimir_modo: "aparte" });
  const [saving, setSaving] = useState(false);
  const [busq, setBusq] = useState("");
  const [resultados, setResultados] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };
  const q = logoTipo ? `?logo_tipo=${encodeURIComponent(logoTipo)}` : "";

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/admin/config-delivery${q}`, { headers }).then(r => r.ok ? r.json() : null).then(c => c && setCfg(c)).catch(() => {});
  }, [token, logoTipo]); // eslint-disable-line

  useEffect(() => {
    if (busq.trim().length < 2) { setResultados([]); return; }
    const t = setTimeout(() => {
      const qs = new URLSearchParams({ search: busq.trim() });
      if (logoTipo) qs.set("logo_tipo", logoTipo);
      fetch(`${API}/admin/productos?${qs}`, { headers })
        .then(r => r.ok ? r.json() : [])
        .then(d => setResultados((Array.isArray(d) ? d : (d.items || d.productos || [])).slice(0, 8)))
        .catch(() => setResultados([]));
    }, 300);
    return () => clearTimeout(t);
  }, [busq]); // eslint-disable-line

  const guardar = async (cambios) => {
    if (!puedeEditar) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/config-delivery`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ logo_tipo: logoTipo || "", ...cambios }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error al guardar"); }
      setCfg(await res.json());
      toast.success("Configuración de delivery guardada");
    } catch (e) { toast.error(e.message || "Error al guardar"); }
    setSaving(false);
  };

  const ids = (cfg.productos || []).map(p => p.producto_id);
  const agregar = (p) => { if (!ids.includes(p.id)) guardar({ producto_ids: [...ids, p.id] }); setBusq(""); setResultados([]); };
  const quitar = (id) => guardar({ producto_ids: ids.filter(x => x !== id) });
  const fmtPrecio = (n) => (n == null ? "" : `₲ ${Math.round(Number(n)).toLocaleString("es-PY")}`);
  const opcion = (lista, actual, key) => (
    <div className="grid md:grid-cols-2 gap-3">
      {lista.map(op => {
        const activa = actual === op.v;
        return (
          <button key={op.v} disabled={saving || !puedeEditar} onClick={() => guardar({ [key]: op.v })}
            className={`text-left rounded-xl border px-4 py-3 transition-all ${activa ? "bg-orange-500/15 border-orange-400/50 text-orange-100" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"} disabled:opacity-60`}>
            <span className="block text-sm font-semibold">{op.t}</span>
            <span className="block text-xs mt-1 text-slate-400">{op.d}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="border border-white/10 rounded-xl p-5 bg-white/3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-orange-500/15 flex items-center justify-center flex-shrink-0">
            <Truck className="w-4 h-4 text-orange-300" />
          </div>
          <div>
            <p className="text-white font-semibold text-sm">Delivery</p>
            <p className="text-slate-400 text-xs mt-0.5 max-w-xl">
              Elegí los productos del catálogo que representan el delivery (ej: delivery sin costo, delivery ₲ 15.000). Al facturar uno de ellos se piden los datos de entrega y se emite la Orden de Delivery con los datos de tu empresa y del cliente.
            </p>
          </div>
        </div>
        {saving && <Loader2 className="w-4 h-4 text-orange-300 animate-spin" />}
      </div>

      <div className="mt-4">
        <label className="text-slate-400 text-xs mb-1 block">Productos de delivery</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {(cfg.productos || []).length === 0 && <span className="text-slate-500 text-xs">Ninguno configurado todavía.</span>}
          {(cfg.productos || []).map(p => (
            <span key={p.producto_id} className="flex items-center gap-1.5 bg-orange-500/10 border border-orange-500/30 text-orange-100 text-xs rounded-lg px-2.5 py-1">
              {p.nombre} {p.precio_venta != null && <span className="text-orange-300/80">({fmtPrecio(p.precio_venta)})</span>}
              {puedeEditar && <button onClick={() => quitar(p.producto_id)} className="text-orange-300 hover:text-white"><X className="w-3 h-3" /></button>}
            </span>
          ))}
        </div>
        {puedeEditar && (
          <div className="relative md:w-96">
            <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-2.5" />
            <input value={busq} onChange={e => setBusq(e.target.value)} placeholder="Buscar producto del catálogo para agregar…"
              className="w-full bg-arandu-dark border border-white/10 rounded-lg pl-8 pr-3 py-2 text-white text-sm" />
            {resultados.length > 0 && (
              <div className="absolute z-20 left-0 right-0 mt-1 bg-arandu-dark border border-white/10 rounded-lg shadow-xl max-h-60 overflow-auto">
                {resultados.map(p => (
                  <button key={p.id} onClick={() => agregar(p)} className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-white/10 flex justify-between gap-3">
                    <span className="truncate">{p.nombre}</span>
                    <span className="text-slate-500 text-xs shrink-0">{fmtPrecio(p.precio_venta)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-4">
        <label className="text-slate-400 text-xs mb-1 block">Formato de la orden de delivery</label>
        <div className="grid md:grid-cols-3 gap-3">
          {FORMATOS.map(op => {
            const activa = cfg.formato === op.v;
            return (
              <button key={op.v} disabled={saving || !puedeEditar} onClick={() => guardar({ formato: op.v })}
                className={`text-left rounded-xl border px-4 py-3 transition-all ${activa ? "bg-orange-500/15 border-orange-400/50 text-orange-100" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"} disabled:opacity-60`}>
                <span className="block text-sm font-semibold">{op.t}</span>
                <span className="block text-xs mt-1 text-slate-400">{op.d}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4">
        <label className="text-slate-400 text-xs mb-1 block">Cuándo se imprime</label>
        {opcion(MODOS, cfg.imprimir_modo, "imprimir_modo")}
      </div>
    </div>
  );
}
